/**
 * Internal Worker Drain Override API Contract (SCH-1449)
 *
 * POST /internal/platform-data-sync/worker-drain   — set or clear the override
 * GET  /internal/platform-data-sync/worker-drain   — read the effective state
 *
 * API-key-gated (`INTERNAL_API_KEY`). On SIGTERM a worker task drains its
 * in-flight sync jobs for `WORKER_DRAIN_DEADLINE_MS` (95 s) before handing the
 * rest back to the queue. This override is the runtime "force" switch the
 * RUNNING workers read at SIGTERM: a system-wide `SystemConfiguration` row
 * (`category: 'sync'`, `key: 'WORKER_DRAIN'`, `value: { drainTimeoutMs }`).
 * `drainTimeoutMs: 0` skips the drain entirely (the pre-SCH-1445 kill-and-
 * redeliver rollout); a smaller positive value shortens it. It can only
 * SHORTEN the drain — the ceiling is the code default, because the ECS stop
 * window is fixed by the task definition.
 *
 * It has to be a runtime read and not a task-definition change because ECS
 * stops an old task with the `stopTimeout` of the revision that task is
 * already running; nothing in the NEW revision can shorten the old one's stop.
 *
 * Force procedure: set `drain_timeout_ms: 0`, deploy, then clear (`null`).
 * The deploy workflow's `force` input does exactly that around the rollout.
 */

import { z } from 'zod'

/** Audit record for the last change, stored in `SystemConfiguration.metadata`. */
export const WorkerDrainAuditSchema = z.object({
  reason: z.string().nullable().describe('Free-text reason supplied by the caller, if any'),
  changedBy: z.string().describe("Caller identity (defaults to 'internal-api')"),
  changedAt: z.string().describe('ISO timestamp of the change')
})

export type WorkerDrainAudit = z.infer<typeof WorkerDrainAuditSchema>

/**
 * POST request body. `drain_timeout_ms` is the override in milliseconds
 * (`0` = no drain); `null` clears the override so workers use the code default.
 */
export const SetWorkerDrainRequestSchema = z.object({
  drain_timeout_ms: z
    .number()
    .int()
    .min(0)
    .nullable()
    .describe('Drain window override in ms (0 = skip the drain); null clears the override'),
  reason: z.string().max(2000).optional().describe('Optional audit reason for the change'),
  changed_by: z
    .string()
    .max(200)
    .optional()
    .describe("Optional caller identity for the audit trail (defaults to 'internal-api')")
})

export type SetWorkerDrainRequest = z.infer<typeof SetWorkerDrainRequestSchema>

/** The effective drain window and where it came from. */
export const WorkerDrainStateSchema = z.object({
  drainTimeoutMs: z.number().int().min(0).describe('Effective drain window a worker will use at SIGTERM'),
  source: z.enum(['override', 'default']).describe('Whether an override row supplied the value'),
  defaultDrainTimeoutMs: z.number().int().min(0).describe('The code default (and the ceiling for an override)'),
  overrideDrainTimeoutMs: z
    .number()
    .int()
    .min(0)
    .nullable()
    .describe('The stored override as written, or null when none is set'),
  audit: WorkerDrainAuditSchema.nullable().describe('Audit record of the last change (null if never set)')
})

export type WorkerDrainState = z.infer<typeof WorkerDrainStateSchema>

export const SetWorkerDrainResponseSchema = z.object({
  message: z.string(),
  data: WorkerDrainStateSchema
})

export type SetWorkerDrainResponse = z.infer<typeof SetWorkerDrainResponseSchema>

export const GetWorkerDrainResponseSchema = z.object({
  data: WorkerDrainStateSchema
})

export type GetWorkerDrainResponse = z.infer<typeof GetWorkerDrainResponseSchema>
